import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { getApi } from "../../utils/api";
import { API_REQUEST_ACCESS_LIST } from "../../config/Endpoints";
import { ThreeDots } from "react-loader-spinner";
import { capitalize, formatDate } from "../../utils/helpers";
import Pagination from "../../components/Pagination";
import Footer from "../../components/Footer";
import SortableTable from "../../components/SortableTable";
import GenerateAccessProgress from "./GenerateAccessProgress";
import Badge from "../../components/Badge";

const AccessRequestList = ({ title, description, onTotalRequestChange }) => {
  const [accessList, setAccessList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [openProgressIndexes, setOpenProgressIndexes] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState();
  const [accessProgressData, setAccessProgressData] = useState({});

  const accessToken = Cookies.get("accessToken");
  const userData = localStorage.getItem("userData");
  const employee = userData ? JSON.parse(userData) : null;
  const employeeId = employee?.id;
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRequest, setTotalRequest] = useState(0);

  const columns = [
    { key: "accessType", label: "Access Type" },
    { key: "type", label: "Role" },
    {
      key: "projectName",
      label: "Project Name",
      render: (row) => (row.projectName ? row.projectName : "All Projects"),
    },
    { key: "dlName", label: "Delivery Lead" },
    { key: "reason", label: "Reason" },
    {
      key: "requestedStartDate",
      label: "Requested Start Date",
      compare: (a, b) =>
        new Date(a._original.requestDate) - new Date(b._original.requestDate),
    },
    {
      key: "requestedEndDate",
      label: "Requested End Date",
      compare: (a, b) =>
        new Date(a._original.requestDate) - new Date(b._original.requestDate),
    },
    {
      key: "status",
      label: "Status",
      render: (item) => {
        const first = item._original.firstApproveStatus;
        const second = item._original.secondApproveStatus;

        // Determine final status label
        const finalStatus =
          first === "REJECTED" ? "Rejected" : second ? capitalize(second) : "";

        // Determine badge color
        const badgeClass =
          first === "REJECTED"
            ? "danger"
            : second === "PENDING"
            ? "warning"
            : second === "REJECTED"
            ? "danger"
            : second === "DEALLOCATED"
            ? "secondary"
            : second === "APPROVED"
            ? "success"
            : "secondary";

        return (
          <>
            {" "}
            <Badge type={badgeClass}>{finalStatus}</Badge>
          </>
        );
      },
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      render: (item) => {
        const index = item._index;

        return (
          <>
            {!openProgressIndexes.includes(index) && (
              <a onClick={() => handleOpenProgress(index, item._original)}>
                View
              </a>
            )}
            {openProgressIndexes.includes(index) && (
              <a
                onClick={() => handleCloseProgress(index)}
                style={{
                  color: "blue",
                  fontWeight: "bolder",
                }}
              >
                Close
              </a>
            )}
          </>
        );
      },
    },
  ];

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (Array.isArray(filteredItems)) {
      setTableData(
        filteredItems.map((item, index) => ({
          accessType: item?.accessType?.name,
          type: item?.type || "-",
          projectName: item.projectName,
          dlName: item.dlName || "-",
          reason: item?.reason,
          requestedStartDate: item?.createdAt
            ? formatDate(item.createdAt)
            : "-",
          requestedEndDate: item?.requestedEndDate
            ? formatDate(item.requestedEndDate)
            : "Ongoing",
          status: item?.secondApproveStatus,
          _original: item,
          _index: index,
        }))
      );
    } else {
      setTableData([]);
    }
  }, [filteredItems]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(e.target.value);
    setCurrentPage(1);
  };

  const handleOpenProgress = (index, item) => {
    if (!openProgressIndexes.includes(index)) {
      setAccessProgressData(item);
      setIsProgressOpen(true);
      setSelectedIndex(index);

      setOpenProgressIndexes([...openProgressIndexes, index]);
    }
  };

  const handleCloseProgress = (index) => {
    setAccessProgressData({});
    setIsProgressOpen(false);
    setSelectedIndex();
    setOpenProgressIndexes(openProgressIndexes.filter((i) => i !== index));
  };

  useEffect(() => {
    fetchList();
  }, [currentPage, limit, employeeId]);

  const fetchList = () => {
    if (!employeeId) return;
    setIsLoading(true);
    getApi(
      `${API_REQUEST_ACCESS_LIST}/${employeeId}?pageSize=${limit}&pageNo=${currentPage}&status=`,
      accessToken
    )
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          const content = res.data.data?.content || [];
          setAccessList(content);
          setFilteredItems(content);
          setTotalPages(res.data.data?.totalPages || 1);
          setTotalRequest(res.data.data?.totalElements);
          if (typeof onTotalRequestChange === "function") {
            onTotalRequestChange(res.data.data?.totalElements || 0);
          }
        }
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
        setFilteredItems([]);
      });
  };

  useEffect(() => {
    if (isProgressOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isProgressOpen]);

  return (
    <>
      <div className="p-3">
        {isLoading && (
          <div className="centered-loader">
            <ThreeDots height="100" width="100" color="blue" visible={true} />
          </div>
        )}

        {!isLoading && filteredItems?.length > 0 && (
          <>
            <SortableTable
              columns={columns}
              data={tableData}
              currentPage={currentPage}
              limit={limit}
            />
            <div className="row">
              <div className="col-md-3">
                <label htmlFor="itemsPerPage">Items per page </label>
                <select
                  id="itemsPerPage"
                  onChange={handleLimitChange}
                  value={limit}
                  className="border ms-3 w-25  p-2"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                </select>
              </div>
              <div className="col-md-9">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </>
        )}

        {!isLoading && filteredItems?.length === 0 && (
          <div className="fs-4 text-secondary text-center">
            <h4>No Record Available</h4>
          </div>
        )}

        <Footer />
      </div>

      {isProgressOpen && (
        <>
          <div
            className="sidebar-backdrop"
            onClick={() => handleCloseProgress(selectedIndex)}
          ></div>

          <aside className="right-sidebar asset-request-progress-sidebar">
            <div
              className="mb-4 page-heading"
              onClick={() => handleCloseProgress(selectedIndex)}
            >
              <h1>{accessProgressData?.accessType?.name}</h1>
              <span className="close_icon">&#10005;</span>
            </div>

            <div className="detail">
              <h5>
                Status:{" "}
                {accessProgressData?.secondApproveStatus === "PENDING"
                  ? "Pending"
                  : accessProgressData?.secondApproveStatus === "REJECTED"
                  ? "Rejected"
                  : accessProgressData?.secondApproveStatus === "APPROVED"
                  ? "Approved"
                  : "-"}
              </h5>
            </div>

            <ul className="track-progress ">
              <li className="approved mb-2">
                <h6>Requested</h6>
              </li>
              {GenerateAccessProgress(accessProgressData)}
            </ul>
          </aside>
        </>
      )}
    </>
  );
};

export default AccessRequestList;
